import type { CoreEnv, CoreRequest } from '@eventbrite/context-gen';
import { DiscoveryEvent } from '@eventbrite/event-renderer';
import {
    getLocaleOverride,
    gettext,
    setLanguage,
    setup as setupI18n,
} from '@eventbrite/i18n';
import { SiteStructure } from '@eventbrite/site-structure';
import * as Sentry from '@sentry/react';
import React from 'react';
import { renderToString } from 'react-dom/server';
import { App } from './app';
import type { ConversionBarProps } from './components/ConversionBar/ConversionBar';
import { ConversionBar } from './components/ConversionBar/ConversionBar';
import { CreatorBanner } from './components/CreatorBanner/CreatorBanner';
import { EventBody } from './components/EventBody/EventBody';
import type { EventHeroComponentProps } from './components/EventHero/EventHero';
import { EventHero } from './components/EventHero/EventHero';
import type { EventDescriptionProps } from './components/EventInfo/EventDescription/EventDescription';
import { EventInfo } from './components/EventInfo/EventInfo';
import type { EventMapLayoutProps } from './components/EventMap/EventMapLayout';
import { EventMapLayout } from './components/EventMap/EventMapLayout';
import { ExpiredEventsPage } from './components/ExpiredEvents/ExpiredEventsPage';
import type { FooterLinks } from './components/Footer/Footer';
import { Footer } from './components/Footer/Footer';
import { MainBodyBackground } from './components/MainBodyBackground/MainBodyBackground';
import type { NotificationBarSsrProps } from './components/NotificationBar/NotificationBarSsr';
import { NotificationBarSsr } from './components/NotificationBar/NotificationBarSsr';
import type { OrganizerPanelLayoutProps } from './components/OrganizerPanel/OrganizerPanelLayout';
import { OrganizerPanelLayout } from './components/OrganizerPanel/OrganizerPanelLayout';
import {
    getShareComponentProps,
    ShareProps,
} from './components/ShareEvent/ShareEventRoot';
import type { ShareInlineProps } from './components/ShareInline/ShareInline';
import type { Tag } from './components/Tags/Tags';
import type {
    AppProps,
    Event,
    EventDetailsProps,
    MainBodyBackgroundScopedProps,
    Organizer,
    Settings,
    User,
} from './types';

const FEATURE_FLAGS = {
    launchCreatorCreditsSectionAttendee: true,
};

export interface ServerAppProps {
    conversionBar: ConversionBarProps;
    user: User;
    event: Event;
    env: CoreEnv;
    request: CoreRequest;
    footerLinks: FooterLinks;
    eventDescription: EventDescriptionProps;
    eventDetails: EventDetailsProps;
    organizer: Organizer;
    tags: Array<Tag>;
    settings: Settings;
    shareInline: ShareInlineProps;
    organizerPanelLayout: OrganizerPanelLayoutProps;
    eventMapLayout: EventMapLayoutProps;
    eventHero: EventHeroComponentProps;
    mainBodyBackground: MainBodyBackgroundScopedProps;
    notificationBar: NotificationBarSsrProps;
    expiredEventsPage: {
        shareComponentProps: ShareProps;
        relatedEvents: DiscoveryEvent[];
    };
}

export const ServerApp = ({
    conversionBar,
    user,
    event,
    env,
    request,
    footerLinks,
    eventDescription,
    eventDetails,
    organizer,
    tags,
    settings,
    shareInline,
    organizerPanelLayout,
    eventMapLayout,
    eventHero,
    notificationBar = {
        message: null,
        type: null,
    },
    mainBodyBackground,
    expiredEventsPage,
}: ServerAppProps) => {
    const { hasCroppedLogo } = event;

    const EventBodyComponent = () => (
        <EventBody>
            <EventHero
                {...{
                    ...eventHero,
                    hasCroppedLogo: event.hasCroppedLogo,
                    isEnded: event.isEnded,
                    isSeriesParent: event.isSeriesParent,
                    hideStartDate: event.hideStartDate,
                    startDateTime: event.startDateTime,
                    startMonthAbbr: event.startMonthAbbr,
                    startDay: event.startDay,
                    hasDonationTicketsAvailable:
                        event.hasDonationTicketsAvailable,
                    eventName: event.name,
                    displayOrganizationName: organizer.displayOrganizationName,
                    organizationNameTruncated: organizer.nameTruncated,
                    orgProfileLink: organizer.url,
                    shouldDisplayRsvp: event.shouldDisplayRsvp,
                }}
            />
            <ConversionBar
                {...{
                    ...conversionBar,
                    externalTickets: {
                        url: conversionBar.externalTicketsUrl,
                        text: conversionBar.externalTicketsButtonText,
                        available: conversionBar.externalTicketsAreAvailable,
                    },
                    id: event.id,
                    childEvents: event.childEvents,
                    isSeriesParent: event.isSeriesParent,
                }}
            />

            <EventInfo
                {...{
                    description: { ...eventDescription },
                    tags,
                    shareInline: { ...shareInline },
                    eventDetails: {
                        ...{
                            ...eventDetails,
                            location: {
                                ...eventDetails.location,
                                isOnlineEvent: event.isOnlineEvent,
                                onlineEventUrl: eventDetails.onlineEvent?.url,
                            },
                            dates: {
                                ...eventDetails.dates,
                                isSeriesParent: event.isSeriesParent,
                                childEvents: event.childEvents || [],
                            },
                        },
                    },
                }}
            />

            <EventMapLayout {...eventMapLayout} />
            <OrganizerPanelLayout {...organizerPanelLayout} />
        </EventBody>
    );

    const MainContent = () => (
        <>
            <div
                className={`js-hidden-when-expired event-listing ${
                    hasCroppedLogo ? 'event-listing--has-image' : ''
                } ${shouldHideMainBody ? 'is-hidden' : ''}`}
                role="article"
            >
                {notificationBar.message && (
                    <NotificationBarSsr {...notificationBar} />
                )}
                <MainBodyBackground
                    hasCroppedLogo={hasCroppedLogo}
                    croppedLogoUrl50={mainBodyBackground.croppedLogoUrl50}
                />
                <div className="g-grid g-grid--page-margin-manual">
                    {user.canManageEvent && !event.isPreview && (
                        <CreatorBanner eventId={event.id} />
                    )}
                    <EventBodyComponent />
                </div>
                {shouldShowBoostBanner && (
                    <div id="listings-root__boost-banner-preview"></div>
                )}
                {shouldShowRelatedEventsLive && (
                    <div id="listings-root__related-events"></div>
                )}
            </div>

            {shouldHideMainBody && (
                <ExpiredEventsPage
                    relatedEvents={expiredEventsPage.relatedEvents}
                    eventName={event.name}
                    organizer={{
                        name: organizer.name,
                        profilePicture: organizer.profilePicture,
                        url: organizer.url,
                    }}
                    isAuthenticated={user.isAuthenticated}
                    shareComponentProps={expiredEventsPage.shareComponentProps}
                />
            )}
        </>
    );
    const shouldHideMainBody =
        !event.isPreview && event.shouldShowRelatedEventsExpired;

    const shouldShowRelatedEventsLive = event.isLive;

    const shouldShowBoostBanner =
        settings.featureFlags?.enable_boost_banner_on_listings &&
        user.canManageEvent;

    const cleanEsiCachePaths = (env: CoreEnv) => {
        const esiPath = user.isAuthenticated
            ? /%2Fesi_nocache/g
            : /%2Fesi_cache/g;
        const removeEsiPath = (string: string): string =>
            string.replace(esiPath, '');

        env.loginUrl = removeEsiPath(env.loginUrl);
        env.logoutUrl = removeEsiPath(env.logoutUrl);
        env.signinUrl = removeEsiPath(env.signinUrl);
        env.signupUrl = removeEsiPath(env.signupUrl);
    };

    cleanEsiCachePaths(env);

    return (
        <Sentry.ErrorBoundary
            fallback={<>{gettext('Error on Listings SSR')}</>}
        >
            {settings.featureFlags?.enable_ssr_for_header ? (
                <SiteStructure
                    env={env}
                    request={request}
                    user={user}
                    preview={event.isPreview}
                    featureFlags={FEATURE_FLAGS}
                    fullScreenMain={true}
                    footer={
                        settings.featureFlags?.enable_ssr_for_footer ? (
                            <Footer {...footerLinks} serveUrl={env.serverUrl} />
                        ) : null
                    }
                >
                    <MainContent />
                </SiteStructure>
            ) : (
                <MainContent />
            )}
        </Sentry.ErrorBoundary>
    );
};

export const render = (props: AppProps) => {
    const locale = getLocaleOverride() || props.env.localeInfo.locale;
    setupI18n(require.context('./i18n/translations', true, /\.json$/));
    setLanguage(locale);

    return renderToString(
        <ServerApp
            user={props.user}
            event={{ ...props.event }}
            request={props.request}
            env={props.env}
            footerLinks={props.footerLinks}
            eventDescription={{ ...props.components.eventDescription }}
            eventDetails={{ ...props.components.eventDetails }}
            tags={props.components.tags}
            shareInline={props.components.shareInline}
            conversionBar={props.components.conversionBar}
            settings={props.settings}
            organizerPanelLayout={props.components.organizerPanel}
            eventMapLayout={props.components.eventMap}
            eventHero={props.components.eventHero}
            organizer={props.organizer}
            mainBodyBackground={props.components.mainBodyBackground}
            notificationBar={props.components.notificationBar}
            expiredEventsPage={{
                ...props.components.relatedEventsExpiredPage,
                shareComponentProps: getShareComponentProps(props),
            }}
        />,
    );
};

export default App;
